// Раздел «Объекты»: иерархия «юрлицо → заказчик → договор → объект → пост» на живых
// серверных данных.
//
// Скелет тот же, что у всех разделов (правило 1): колонка списка с переключателем
// представлений и фильтрами, колонка детали с вкладками. Новых страниц раздел не заводит —
// три представления и одна карточка с четырьмя вкладками.
//
// Всё состояние экрана лежит в адресе (правило 4): представление, поиск, фильтры, выбранная
// строка, вкладка карточки и открытый мастер. Ссылка, отправленная коллеге, открывается у
// него ровно тем же экраном.

// Полоса вкладок, а не сегментированный переключатель. Правка 03.09.2026, общая на весь продукт.
//
// Сегментированный раскладывает доли РАВНЫМИ и запрещает перенос текста: как только пунктов
// становится больше, чем помещается, названия налезают друг на друга. В «Найме» это уже
// случилось — восемь названий в колонке 416 точек рисовались одно поверх другого, — а в
// «Деньгах» стояло в шаге от этого: «Очередь приёмки» требовала 115 точек при доле в 106.
//
// Граница теперь по смыслу, а не по числу пунктов: сегментированный остаётся там, где человек
// выбирает РЕЖИМ из двух-трёх равных (порядок сортировки на пульте, способ ведения табеля);
// переход между ПРЕДСТАВЛЕНИЯМИ раздела везде идёт полосой вкладок. У неё ширина по
// содержимому и прокрутка, то есть наложение невозможно по устройству. Держит
// `e2e/переключатель-читается.spec.ts` — замером координат, а не чтением исходника: пункты
// собираются циклом по правам, и в тексте файла их не пересчитать.

import { RiCompass3Line } from '@remixicon/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import { useAuthStore } from '@/shared/auth/store';
import { canAny } from '@/shared/rbac/permissions';
import { parseSearch } from '@/shared/routing/search';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState } from '@/shared/ui/screen-state';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import { useStaffing } from '../payroll/queries';

import { CustomerCard } from './card/CustomerCard';
import { ЧтоСОбъектами } from './components/ЧтоСОбъектами';
import { PostCard } from './card/PostCard';
import { SiteCard } from './card/SiteCard';
import type { SitesRoute, SitesView } from './route';
import {
  DEFAULT_VIEW,
  FILTER_SITE,
  isSitesView,
  openNewSiteHref,
  sitesHref,
  switchViewHref,
} from './route';
import { CustomersList } from './views/CustomersList';
import { ObjectsList } from './views/ObjectsList';
import { PostsList } from './views/PostsList';

// Побочный эффект: раздел объявляет каркасу свои панели-мастера.
import './panels/register';

const VIEW_TITLES: Readonly<Record<SitesView, string>> = {
  objects: 'Объекты',
  customers: 'Заказчики',
  posts: 'Посты',
};

export function SitesSection({ view: viewFromShell }: { view?: string }) {
  const params = useParams<{ view?: string; rowId?: string }>();
  const location = useLocation();

  const rawView = params.view ?? viewFromShell;
  const search = parseSearch(location.search);

  if (rawView !== undefined && !isSitesView(rawView)) {
    return (
      <main className='flex h-full flex-1 items-center justify-center bg-bg-weak-50'>
        <EmptyState
          icon={RiCompass3Line}
          title={`Представления «${rawView}» в разделе «Объекты» нет`}
          description='В разделе три представления: объекты, заказчики и посты. Похоже, ссылка устарела или в ней опечатка.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={sitesHref(DEFAULT_VIEW, { filters: {}, panels: [] })}>
                К реестру объектов
              </Link>
            </LinkButton.Root>
          }
        />
      </main>
    );
  }

  const view: SitesView = isSitesView(rawView) ? rawView : DEFAULT_VIEW;
  const route: SitesRoute = { view, rowId: params.rowId, search };

  const viewSwitcher = <ViewSwitcher route={route} />;

  return (
    <>
      {view === 'objects' && <ObjectsList route={route} viewSwitcher={viewSwitcher} />}
      {view === 'customers' && <CustomersList route={route} viewSwitcher={viewSwitcher} />}
      {view === 'posts' && <PostsList route={route} viewSwitcher={viewSwitcher} />}
      <SectionDetail route={route} />
    </>
  );
}

/** Переключатель представлений. Одно представление — одна грамматика строки (правило 2). */
function ViewSwitcher({ route }: { route: SitesRoute }) {
  const navigate = useNavigate();
  return (
    <TabMenu.Root
      value={route.view}
      onValueChange={(next) => {
        if (isSitesView(next)) {
          navigate(switchViewHref(route.view, next, route.search, route.rowId));
        }
      }}
    >
      <TabMenu.List className='h-10 gap-5 border-t-0'>
        {(Object.keys(VIEW_TITLES) as SitesView[]).map((key) => (
          <TabMenu.Trigger key={key} value={key}>
            {VIEW_TITLES[key]}
          </TabMenu.Trigger>
        ))}
      </TabMenu.List>
    </TabMenu.Root>
  );
}

/**
 * Правая колонка «Объектов», пока объект не выбран.
 *
 * Вместо «Выберите объект» — ответ на первый вопрос операционного руководителя: где не хватает
 * людей. До 04.09.2026 здесь стоял абзац на 420 точек посреди тысячи, и сказано в нём было
 * ровно то, что человек и так видит.
 *
 * Отчёт требует своего права, и у кого его нет — видит прежнюю просьбу выбрать объект: пустая
 * колонка честнее выдуманного содержимого. Пока отчёт едет, тоже прежняя: подменять просьбу
 * каркасом ради полусекунды значит мигать на ровном месте.
 */
function БезВыбранногоОбъекта({ route }: { route: SitesRoute }) {
  const permissions = useAuthStore((state) => state.permissions);
  // Отдельным компонентом, а не условием внутри: у кого права нет — за отчётом не идут вовсе.
  // Запрос «на всякий случай» вернул бы отказ, и человек получил бы его в журнале сети на
  // каждом открытии раздела.
  if (canAny(permissions, ['core.report.view', 'core.dashboard.view'])) {
    return <ЧтоСОбъектамиИлиПросьба route={route} />;
  }
  return <ПросьбаВыбратьОбъект route={route} />;
}

function ЧтоСОбъектамиИлиПросьба({ route }: { route: SitesRoute }) {
  // Кнопка заведения стоит и здесь, и в пустом списке слева: право на неё сторожит сама
  // панель мастера, как и раньше — своего кода разрешения у объектов в договоре нет.
  const сводка = useStaffing();

  // Пока отчёт едет — прежняя просьба. Подменять её каркасом ради полусекунды значит мигать
  // на ровном месте; отказ отчёта тоже не повод показывать пустоту вместо понятного текста.
  if (сводка.data === undefined) {
    return <ПросьбаВыбратьОбъект route={route} />;
  }
  return (
    <DetailPane>
      <ЧтоСОбъектами
        сводка={сводка.data}
        ссылка={(siteId) => sitesHref('objects', route.search, siteId)}
        заведение={openNewSiteHref(route)}
      />
    </DetailPane>
  );
}

function ПросьбаВыбратьОбъект({ route }: { route: SitesRoute }) {
  return (
    <DetailPane>
      <div className='flex h-full items-center justify-center'>
        <EmptyState
          title='Объект не выбран'
          description='Выберите объект в списке слева — здесь откроется его карточка: заказчик и договор, посты, норматив часов и укомплектованность.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={openNewSiteHref(route)}>Завести новый объект</Link>
            </LinkButton.Root>
          }
        />
      </div>
    </DetailPane>
  );
}

/** Колонка детали: что открыто в текущем представлении и что показать, когда не открыто. */
function SectionDetail({ route }: { route: SitesRoute }) {
  const { view, rowId, search } = route;

  if (view === 'objects') {
    if (rowId === undefined) {
      return <БезВыбранногоОбъекта route={route} />;
    }
    return <SiteCard siteId={rowId} route={route} />;
  }

  if (view === 'customers') {
    if (rowId === undefined) {
      return (
        <DetailPane>
          <div className='flex h-full items-center justify-center'>
            <EmptyState
              title='Заказчик не выбран'
              description='Выберите заказчика в списке слева — здесь откроются его отрасль, регион, контакт и переход к его объектам.'
            />
          </div>
        </DetailPane>
      );
    }
    return <CustomerCard customerId={rowId} route={route} />;
  }

  const siteId = search.filters[FILTER_SITE];
  // Объект не выбран — просьба выбрать его уже стоит в колонке слева, со ссылкой на реестр.
  // Повторять её здесь значит написать на одном экране одно и то же дважды разными словами:
  // «Выберите объект» слева и «Объект не выбран» справа. Человек читает это как два разных
  // сообщения и ищет разницу, которой нет. Колонка детали остаётся пустой поверхностью — без
  // текста, но и без выдуманного содержимого.
  if (siteId === undefined) {
    return <DetailPane>{null}</DetailPane>;
  }
  if (rowId === undefined) {
    return (
      <DetailPane>
        <div className='flex h-full items-center justify-center'>
          <EmptyState
            title='Пост не выбран'
            description='Выберите пост в списке слева — здесь откроются его часы в сутки, разряд и число охранников одновременно.'
          />
        </div>
      </DetailPane>
    );
  }
  return <PostCard postId={rowId} siteId={siteId} route={route} />;
}
